import type { RenderType } from './render';
import type { Agent, TaskPlanTask, StreamParseType } from './base';
import type { AgentUnionCode } from './agent';
import type { LLMModel } from '@eureka/shared/select-llm';

export enum SSE_EVENT_TYPE {
    DEFAULT = 'default',
    AGENT_INIT = 'agent_init',
    PLAN_START = 'plan_start',
    PLAN_END = 'plan_end',
    MESSAGE = 'message',
    SELF_MESSAGE = 'self_message',
    TASK_READY = 'task_ready',
    TASK_START = 'task_start',
    TASK_COMPLETED = 'task_completed',
    TASK_END = 'task_end',
    AGENT_READY = 'agent_ready',
    AGENT_START = 'agent_start',
    AGENT_END = 'agent_end',
    AGENT_ACTION = 'agent_action',
    TOOL_START = 'tool_start',
    TOOL_END = 'tool_end',
    RESULT_AS_ANSWER = 'result_as_answer',
    CREW_START = 'crew_start',
    CREW_END = 'crew_end',
    STATUS = 'status',
    STREAM_MESSAGE = 'stream_message',
    STREAM_MESSAGE_START = 'stream_message_start',
    STREAM_MESSAGE_END = 'stream_message_end',
    TOOL_STREAM_MESSAGE = 'tool_stream_message',
    TOOL_STREAM_MESSAGE_START = 'tool_stream_message_start',
    TOOL_STREAM_MESSAGE_END = 'tool_stream_message_end',
    ERROR = 'error',
    WAITING_FOR_USER_INPUT = 'waiting_for_user_input',
    USER_NO_INPUT = 'user_no_input',
    USER_INPUT_RECEIVED = 'user_input_received',
    CHAT_GUIDE = 'chat_guide',
    CHAT_COMPLETION_QUESTION = 'chat_completion_question',
    CHAT_COMPLETION_MESSAGE = 'chat_completion_message',
    CHAT_START = 'chat_start',
    CHAT_END = 'chat_end',
    CHAT_STREAM = 'chat_stream',
    CHAT_STREAM_START = 'chat_stream_start',
    CHAT_STREAM_END = 'chat_stream_end',

    PLAN_STREAM_START = 'plan_stream_start',
    PLAN_STREAM_END = 'plan_stream_end',
    PLAN_COMPLETION_MESSAGE = 'plan_completion_message',
    PLAN_STREAM = 'plan_stream',
    PLAN_COMPLETE = 'plan_complete',

    EXECUTION_START = 'execution_start',

    THINK_STREAM_MESSAGE_START = 'think_stream_message_start',
    THINK_STREAM_MESSAGE = 'think_stream_message',
    THINK_STREAM_MESSAGE_END = 'think_stream_message_end',

    DIRECT_ANSWER_STREAM_MESSAGE_START = 'direct_answer_stream_message_start',
    DIRECT_ANSWER_STREAM_MESSAGE = 'direct_answer_stream_message',
    DIRECT_ANSWER_STREAM_MESSAGE_END = 'direct_answer_stream_message_end',

    FINAL_ANSWER_STREAM_MESSAGE_START = 'final_answer_stream_message_start',
    FINAL_ANSWER_STREAM_MESSAGE = 'final_answer_stream_message',
    FINAL_ANSWER_STREAM_MESSAGE_END = 'final_answer_stream_message_end',

    JOB_TERMINATED = 'job_terminated',

    TEAM_TOOL_START = 'team_tool_start',
    TEAM_TOOL_END = 'team_tool_end',

    JOB_INIT_FAILED = 'job_init_failed'
}

export interface JobInitFailedData {
    type: SSE_EVENT_TYPE.JOB_INIT_FAILED
}
export interface TeamToolStartData {
    type: SSE_EVENT_TYPE.TEAM_TOOL_START
    time: string
    goal?: string
    avg_time?: number
    agent_name: string
    tool_name: string
    tool_alias?: string
    tool_id: number | string
    tool_call_id?: string
    tool_definition_id?: number
    event_id: string
    view_render: RenderType
    result_as_answer?: boolean
    thought: string
    coworker?: string
}

export interface TeamToolEndData {
    type: SSE_EVENT_TYPE.TEAM_TOOL_END
    time: string
    agent_name: string
    tool_name: string
    tool_alias?: string
    event_id: string
    coworker?: string
}

export interface JobTerminatedData {
    type: SSE_EVENT_TYPE.JOB_TERMINATED
    time: string
    event_id: string
}

export interface FinalAnswerStreamMessageStartData {
    type: SSE_EVENT_TYPE.FINAL_ANSWER_STREAM_MESSAGE_START
    time: string
    event_id: string
    agent_code?: string
}

export interface FinalAnswerStreamMessageData {
    type: SSE_EVENT_TYPE.FINAL_ANSWER_STREAM_MESSAGE
    time: string
    event_id: string
    content: string
    is_total_message: boolean
    agent_code?: string
}

export interface FinalAnswerStreamMessageEndData {
    type: SSE_EVENT_TYPE.FINAL_ANSWER_STREAM_MESSAGE_END
    time: string
    event_id: string
}

export interface DirectAnswerStreamMessageStartData {
    type: SSE_EVENT_TYPE.DIRECT_ANSWER_STREAM_MESSAGE_START
    time: string
    event_id: string
}

export interface DirectAnswerStreamMessageData {
    type: SSE_EVENT_TYPE.DIRECT_ANSWER_STREAM_MESSAGE
    time: string
    event_id: string
    content: string
    is_total_message: boolean
}

export interface DirectAnswerStreamMessageEndData {
    type: SSE_EVENT_TYPE.DIRECT_ANSWER_STREAM_MESSAGE_END
    time: string
    event_id: string
}

export interface ThinkStreamMessageStartData {
    type: SSE_EVENT_TYPE.THINK_STREAM_MESSAGE_START
    time: string
    event_id: string
}

export interface ThinkStreamMessageData {
    type: SSE_EVENT_TYPE.THINK_STREAM_MESSAGE
    time: string
    event_id: string
    content: string
    is_total_message: boolean
}

export interface ThinkStreamMessageEndData {
    type: SSE_EVENT_TYPE.THINK_STREAM_MESSAGE_END
    time: string
    event_id: string
}

export interface ExecutionStartData {
    type: SSE_EVENT_TYPE.EXECUTION_START
    time: string
    event_id: string
    plan: TaskPlanTask[]
}

export interface PlanStreamStartData {
    type: SSE_EVENT_TYPE.PLAN_STREAM_START
    time: string
    event_id: string
}

export interface PlanStreamEndData {
    type: SSE_EVENT_TYPE.PLAN_STREAM_END
    time: string
    event_id: string
}

export interface PlanCompletionMessageData {
    type: SSE_EVENT_TYPE.PLAN_COMPLETION_MESSAGE
    time: string
    event_id: string
    content: string
}

export interface PlanStreamData {
    type: SSE_EVENT_TYPE.PLAN_STREAM
    content: string
    time: string
    event_id: string
}

export interface PlanCompleteData {
    type: SSE_EVENT_TYPE.PLAN_COMPLETE
    time: string
    event_id: string
    plan: string
}

export interface PlanStartData {
    type: SSE_EVENT_TYPE.PLAN_START
    event_id: string
    time: string
}

export interface PlanEndData {
    type: SSE_EVENT_TYPE.PLAN_END
    event_id: string
    time: string
}

export interface UserInputReceivedData {
    type: SSE_EVENT_TYPE.USER_INPUT_RECEIVED
    agent_role: string
    event_id?: string
    time: string
}

export interface WaitingForUserInputData {
    type: SSE_EVENT_TYPE.WAITING_FOR_USER_INPUT
    agent_role: string
    event_id?: string
    time: string
    wait_time: number
}

export interface UserNoInputData {
    type: SSE_EVENT_TYPE.USER_NO_INPUT
    agent_role: string
    event_id?: string
    time: string
}

export enum STATUS_TYPE {
    STARTED = 'started',
    FAILED = 'failed'
}

export interface AgentInitData {
    content: Agent
    type: SSE_EVENT_TYPE.AGENT_INIT
    input?: string
}

export interface StatusData {
    type: SSE_EVENT_TYPE.STATUS
    status: STATUS_TYPE
    event_id: string
}

export interface ErrorData {
    type: SSE_EVENT_TYPE.ERROR
    error_code?: number
    agent_codes?: Array<{
        product_type: string
        agent_code: string
        agent_id: string
        agent_name_cn: string
        agent_name_en: string
    }>
    agents: Agent[]
    message: string
    event_id: string
    time?: string
}

export interface StreamMessageData {
    type: SSE_EVENT_TYPE.STREAM_MESSAGE
    time?: string
    content: string
    event_id?: string
}

export interface StreamMessageStartData {
    type: SSE_EVENT_TYPE.STREAM_MESSAGE_START
    time?: string
    event_id?: string
}

export interface StreamMessageEndData {
    type: SSE_EVENT_TYPE.STREAM_MESSAGE_END
    time?: string
    event_id?: string
}

export interface ToolStreamMessageData {
    type: SSE_EVENT_TYPE.TOOL_STREAM_MESSAGE
    time?: string
    content: string
    event_id?: string
    is_total_message?: boolean
}

export interface ToolStreamMessageStartData {
    type: SSE_EVENT_TYPE.TOOL_STREAM_MESSAGE_START
    time?: string
    event_id?: string
}

export interface ToolStreamMessageEndData {
    type: SSE_EVENT_TYPE.TOOL_STREAM_MESSAGE_END
    time?: string
    event_id?: string
}

export interface MessageData {
    type: SSE_EVENT_TYPE.MESSAGE
    time?: string
    message: string
    event_id?: string
}

export interface SelfMessageData {
    type: SSE_EVENT_TYPE.SELF_MESSAGE
    time?: string
    message: string
    event_id?: string
}

export interface TaskReadyData {
    type: SSE_EVENT_TYPE.TASK_READY
    time: string
    task_name: string
    event_id: string
}

export interface TaskStartData {
    type: SSE_EVENT_TYPE.TASK_START
    time: string
    name: string
    task_description: string
    task_id: number
    event_id: string
}

export interface ResultAsAnswerData {
    type: SSE_EVENT_TYPE.RESULT_AS_ANSWER
    time: string
    event_id: string
}

export interface TaskCompletedData {
    type: SSE_EVENT_TYPE.TASK_COMPLETED
    time: string
    task_name: string
    task_summary: string
    task_result: string
    agent_code: string
    output_format: string
    task_id: string | number
    event_id: string
}

export interface TaskEndData {
    type: SSE_EVENT_TYPE.TASK_END
    time: string
    task_description: string
    task_id: number
    event_id: string
    agent_code?: string
}

export interface AgentReadyData {
    type: SSE_EVENT_TYPE.AGENT_READY
    time: string
    agent_role: string
    agent_goal: string
    event_id: string
}

export interface AgentStartData {
    type: SSE_EVENT_TYPE.AGENT_START
    time: string
    agent_role: string
    brief_title?: string
    event_id: string
}

export interface AgentEndData {
    type: SSE_EVENT_TYPE.AGENT_END
    time: string
    agent_role: string
    event_id: string
}

export interface AgentActionData {
    type: SSE_EVENT_TYPE.AGENT_ACTION
    time: string
    agent_role: string
    result: {
        tool_name: string,
        status: string,
        results: string | Record<string, any>
        view_render?: RenderType,
        result_template?: string,
        parameters_used?: {
            question?: string,
            text?: string,
        }
    }
    thought: string
    tool: string
    event_id: string
}

export interface ToolStartData {
    type: SSE_EVENT_TYPE.TOOL_START
    time: string
    goal?: string
    avg_time?: number
    agent_name: string
    tool_name: string
    tool_alias?: string
    tool_id: number | string
    tool_call_id?: string
    tool_definition_id?: number
    event_id: string
    view_render: RenderType
    result_as_answer?: boolean
    thought: string
    stream_parse_type?: StreamParseType
}

export interface ToolEndData {
    type: SSE_EVENT_TYPE.TOOL_END
    time: string
    agent_name: string
    tool_name: string
    tool_alias?: string
    event_id: string
}

export interface CrewStartData {
    type: SSE_EVENT_TYPE.CREW_START
    time: string
    crew_name: string
    event_id: string
}

export interface CrewEndData {
    type: SSE_EVENT_TYPE.CREW_END
    time: string
    crew_name: string
    event_id: string
}

export interface ChatGuideData {
    type: SSE_EVENT_TYPE.CHAT_GUIDE
    time?: string
    info: {
        title: string
        content: string
        examples: string[]
        description?: string
        goal?: string
    }
    event_id?: string
}

export enum ChatCompletionQuestionLevel {
    GOOD = 'good',
    NORMAL = 'normal',
    BAD = 'bad'
}

export interface ChatCompletionQuestionData {
    type: SSE_EVENT_TYPE.CHAT_COMPLETION_QUESTION
    time?: string
    info: Partial<{
        content: string
        examples: string[]
        level: ChatCompletionQuestionLevel
        question: string
        score: number
        title: string
        ready: 'true' | 'false'
    }>
    event_id?: string
}

export interface ChatCompletionMessageData {
    type: SSE_EVENT_TYPE.CHAT_COMPLETION_MESSAGE
    time?: string
    content: string
    message_id: string
    event_id?: string
    is_total_message: boolean
}

export interface ChatStreamEndData {
    type: SSE_EVENT_TYPE.CHAT_STREAM_END
    time?: string
    event_id?: string
}

export interface AgentAskTrackData {
    agent_job_id: string
    input?: string
    code?: AgentUnionCode
    model?: LLMModel
    work_project?: string
    summary_check?: boolean
    mode?: 'planing' | 'qa'
}

// 整体event类型集合
export type EventData =
    AgentInitData |
    MessageData |
    SelfMessageData |
    TaskReadyData |
    TaskStartData |
    TaskCompletedData |
    ResultAsAnswerData |
    TaskEndData |
    AgentReadyData |
    AgentStartData |
    AgentEndData |
    AgentActionData |
    ToolStartData |
    ToolEndData |
    CrewStartData |
    CrewEndData |
    StatusData |
    StreamMessageData |
    StreamMessageStartData |
    StreamMessageEndData |
    ToolStreamMessageData |
    ToolStreamMessageStartData |
    ToolStreamMessageEndData |
    ErrorData |
    WaitingForUserInputData |
    UserNoInputData |
    UserInputReceivedData |
    ChatGuideData |
    ChatCompletionQuestionData |
    ChatCompletionMessageData |
    PlanStartData |
    PlanEndData |
    PlanStreamStartData |
    PlanStreamEndData |
    PlanCompletionMessageData |
    PlanStreamData |
    PlanCompleteData |
    ExecutionStartData |
    ThinkStreamMessageStartData |
    ThinkStreamMessageData |
    ThinkStreamMessageEndData |
    DirectAnswerStreamMessageStartData |
    DirectAnswerStreamMessageData |
    DirectAnswerStreamMessageEndData |
    FinalAnswerStreamMessageStartData |
    FinalAnswerStreamMessageData |
    FinalAnswerStreamMessageEndData |
    ChatStreamEndData |
    TeamToolStartData |
    TeamToolEndData |
    JobTerminatedData |
    JobInitFailedData;
